import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { EventPipelineService } from '@bootstrap/services/event-pipeline.service';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { ImplantsViewDataChanged, EventName } from '@itero/itero-client-communication';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA } from '@itero/ui-toolkit-angular/modal-window';
import { TypeEnum } from '@modules/order/models/type.enum';
import { getToothNumberById } from '@modules/teeth-diagram/models/teeth-numbering';
import { TeethNumberingSystem } from '@modules/teeth-diagram/models/teeth-numbering-system.enum';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { ToothEditorModalActions } from '@modules/teeth-diagram/models/tooth-editor-actions.enum';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { ToothEditorComponentBase } from '@modules/tooth-editor/base/tooth-editor-component-base';
import { ImplantsViewAppParameter } from '@shared/implants-view/models/implants-view-app-parameter';
import { ToothEditorSharingService } from '@modules/tooth-editor/services/tooth-editor-sharing.service';
import { DefaultCrownStore } from '@modules/tooth-editor/state/default-crown-store';
import { TeethManagerService } from '@modules/tooth-editor/state/teeth-manager-service';
import { ToothEditorFacade } from '@modules/tooth-editor/tooth-editor.facade';
import { RestorationTypeIds } from '@modules/tooth-editor/restoration-type.facade';
import { TranslateService } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { PopupIconNames } from '@shared/models/enums/popup-icon.enum';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { IdName } from '@shared/models/id-name';
import { PositionObject } from '@shared/models/position-object';
import { CopyToothService } from '@shared/services/copyTooth.service';
import { ForceSaveToothEditorService } from '@shared/services/force-save-tooth-editor.service';
import { PopupService } from '@shared/services/popup.service';
import { extractPositionObject } from '@shared/services/position-object-extractor';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

@Component({
	selector: 'rx-tooth-editor',
	styleUrls: ['./tooth-editor.component.scss'],
	templateUrl: 'tooth-editor.component.html',
	providers: [ToothEditorFacade],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToothEditorComponent extends BaseDestroyableComponent implements OnInit, ToothEditorComponentBase {
	readonly emptyToothClickedOnValues = {
		MaterialID: null,
		PreparationDesignBuccalId: null,
		PreparationDesignBuccal: null,
		PreparationDesignLingualId: null,
		PreparationDesignLingual: null,
		MarginDesignBuccalId: null,
		MarginDesignLingualId: null,
		ShadeSystemId: null,
		ShadeIncisal: null,
		ShadeBody: null,
		ShadeGingival: null,
		StumpfShade: null,
		AbutmentType: null,
		AbutmentMaterialId: null,
		CAMaterialID: null,
		AbutmentMarginStyle: null,
		AbutmentMarginStyleId: null,
		CAMarginStyle: null,
		CAMarginStyleId: null,
		ImplantTypeID: null,
		SpecificationId: SpecificationEnum.None
	};

	implantsViewAppParameters$: Observable<ImplantsViewAppParameter> = this.toothEditorFacade.implantsViewAppParameters$;
	shouldShowNewImplantsView$: Observable<boolean> = this.toothEditorFacade.shouldShowNewImplantsView$;
	getToothNumberById = getToothNumberById;
	teethNumberingSystem$: Observable<TeethNumberingSystem> = this.toothEditorFacade.teethNumberingSystem$;
	teeth$: Observable<Tooth[]> = this.toothEditorFacade.teeth$;
	isBridge$: Observable<boolean> = this.toothEditorFacade.isBridge$;
	toothClickedOn$: Observable<Tooth> = this.toothEditorFacade.toothClickedOn$.pipe(filter((tooth): tooth is Tooth => !!tooth));
	unitTypeMenuInput$: Observable<[IdName[], { tooth: Tooth; positionObj: PositionObject }]>;
	isImplantBased$: Observable<boolean> = this.toothClickedOn$.pipe(
		map(tooth => this.toothEditorFacade.checkIsImplantBased(tooth)),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	isImplantPosition$: Observable<boolean> = this.toothClickedOn$.pipe(
		map(tooth => this.toothEditorFacade.checkIsImplantPosition(tooth)),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	isReadOnly$: Observable<boolean> = this.toothEditorFacade.isReadOnly$;
	showImplantAbutmentDisclaimerBanner$: Observable<boolean> = combineLatest([this.isReadOnly$, this.shouldShowNewImplantsView$]).pipe(
		map(([isReadOnly, shouldShowNewImplantsView]) => isReadOnly === false && !shouldShowNewImplantsView)
	);
	unitTypeName$: Observable<string> = this.toothClickedOn$.pipe(
		map(toothClickedOn =>
			this.translateService.instant(`UnitTypes.${toothClickedOn.UnitTypeID ? UnitTypes[toothClickedOn.UnitTypeID] : 'Bridge'}`)
		)
	);

	TypeEnum = TypeEnum;

	get procedureMap() {
		return this.toothEditorFacade.procedureMap;
	}

	get isGlidewellOrder(): boolean {
		return this.toothEditorFacade.isGlidewellOrder;
	}

	constructor(
		public dialogRef: TlkModalWindowRef<{ action: ToothEditorModalActions; teeth: Tooth[] }, ToothEditorComponent>,
		public toothEditorFacade: ToothEditorFacade,
		private translateService: TranslateService,
		private snackBar: MatSnackBar,
		private popupService: PopupService,
		private forceSaveToothEditorService: ForceSaveToothEditorService,
		public toothEditorSharingService: ToothEditorSharingService,
		@Inject(TLK_MODAL_WINDOW_DATA)
		public data: {
			unitType: number;
			teeth: Tooth[];
			toothClickedOn: Tooth;
			allTeethInJaw: Tooth[];
			isBridge: boolean;
		},
		private copyToothService: CopyToothService,
		private defaultCrownStore: DefaultCrownStore,
		private readonly teethManagerService: TeethManagerService,
		private readonly eventPipelineService: EventPipelineService
	) {
		super();
		dialogRef.disableClose = true;
	}

	ngOnInit(): void {
		this.toothEditorFacade.initTeeth({ teeth: this.data.teeth });
		this.toothEditorFacade.updateToothClickedOn({ toothClickedOn: this.data.toothClickedOn });
		this.toothEditorFacade.updateAllTeethInJaw({ allTeethInJaw: this.data.allTeethInJaw });
		this.toothEditorFacade.updateIsBridge({ isBridge: this.data.isBridge });
		this.forceSaveToothEditorService.forceSaveToothEditor$
			.pipe(
				tap(forceSave => {
					if (forceSave) {
						this.forceSave();
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
		this.eventPipelineService
			.fromEvent<ImplantsViewDataChanged>(EventName.implantsViewDataChanged)
			.pipe(
				withLatestFrom(this.toothClickedOn$),
				takeUntil(this.componentAlive$),
				filter(([event, tooth]) => tooth.ToothID === event.data.toothNumber)
			)
			.subscribe(([event, tooth]) => this.handleImplantsViewDataChangedEvent(event, tooth));
	}

	handleToothClick({ tooth, event }: { tooth: Tooth; event: any }): void {
		const positionObj: PositionObject = extractPositionObject({ event });

		if (tooth.BridgeIndex) {
			this.handleToothClickInBridge(tooth, positionObj);
		} else {
			this.handleSingleToothClick(tooth, positionObj);
		}
	}

	handleToothClickInBridge(tooth: Tooth, positionObj: PositionObject): void {
		const unitTypes$ = this.toothEditorFacade.getUnitTypeNamesForBridge(tooth);

		const toothClickObj$ = of({ tooth, positionObj });

		this.unitTypeMenuInput$ = combineLatest([unitTypes$, toothClickObj$]);

		if (tooth.ToothInBridgeTypeID) {
			this.switchToTheClickedTooth(tooth);
		}
	}

	handleSingleToothClick(tooth: Tooth, positionObj: PositionObject): void {
		const unitTypes$ = this.toothEditorFacade.getUnitTypeNames(tooth);

		const toothClickObj$ = of({ tooth, positionObj });

		this.unitTypeMenuInput$ = combineLatest([unitTypes$, toothClickObj$]);
	}

	switchToTheClickedTooth(tooth: Tooth): void {
		this.toothEditorFacade.updateToothClickedOn({ toothClickedOn: tooth });
	}

	handleUnitTypeInBridgeSelection({ unitTypeInBridge, tooth }: { unitTypeInBridge: UnitTypesInBridge; tooth: Tooth }): void {
		// Prevent selecting Implant Based when offline (only if new implants view should be shown)
		if (this.toothEditorFacade.shouldPreventImplantBasedInBridgeSelection(unitTypeInBridge)) {
			return;
		}

		this.teethManagerService.markAsAssignedByUser(tooth, 'ToothInBridgeTypeID');

		const toothAndSharedProps = this.toothEditorSharingService.getToothWithSharedProps(this.toothEditorFacade.teeth, {
			...this.emptyToothClickedOnValues,
			...tooth,
			// ShadeSystem needs to be provided to allow body copying
			ShadeSystemId: this.toothEditorFacade.getShadeSystemOptionsAndSelectionForV1(tooth.ShadeSystemId as any).shadeSystem.Id,
			ToothInBridgeTypeID: unitTypeInBridge
		});
		let updatedBridgeTooth = toothAndSharedProps.tooth;
		const convertedUnitType = this.toothEditorFacade.convertFromBridgeToothUnitType({ unitTypeInBridge });

		updatedBridgeTooth = this.toothEditorSharingService.resetToothIfNeeded(updatedBridgeTooth, convertedUnitType as any);

		this.teethManagerService.markAsShared(toothAndSharedProps);
		this.toothEditorFacade.updateToothClickedOn({ toothClickedOn: updatedBridgeTooth });
		this.toothEditorFacade.updateTooth({ tooth: updatedBridgeTooth });
	}

	handleUnitTypeSelection({ unitType, tooth }: { unitType: number; tooth: Tooth }): void {
		// Prevent selecting Implant Based when offline (only if new implants view should be shown)
		if (this.toothEditorFacade.shouldPreventImplantBasedSelection(unitType)) {
			return;
		}

		this.teethManagerService.markAsAssignedByUser(tooth, 'UnitTypeID');

		const clearToothData = [
			UnitTypes.Regular,
			UnitTypes.Detachable,
			UnitTypes.Missing_edentulousSpace,
			UnitTypes.Missing_noSpace
		].includes(unitType);

		let toothToUpdate: Tooth = tooth;

		if (clearToothData) {
			toothToUpdate = {
				...getNewTooth(),
				ToothID: tooth.ToothID,
				UnitTypeID: tooth.UnitTypeID,
				ToothInBridgeTypeID: tooth.ToothInBridgeTypeID
			};
		}
		toothToUpdate = this.toothEditorSharingService.resetToothIfNeeded(toothToUpdate, unitType);

		const updatedBridgeTooth = {
			...toothToUpdate,
			...this.emptyToothClickedOnValues,
			UnitTypeID: unitType
		};

		this.toothEditorFacade.updateTooth({ tooth: updatedBridgeTooth });
	}

	handleModalClose({ action }: { action: ToothEditorModalActions }): void {
		combineLatest([this.teeth$, this.isBridge$])
			.pipe(
				take(1),
				tap(([teeth, isBridge]: [Tooth[], boolean]) => {
					if (isBridge) {
						if (action === ToothEditorModalActions.Apply) {
							const bridgeValidation = this.toothEditorFacade.validateBridge({ teeth });

							if (bridgeValidation.isValid) {
								this.dialogRef.close({ action, teeth });
							} else {
								this.snackBar.open(bridgeValidation.validationMessage ?? '', undefined, {
									duration: 2000,
									horizontalPosition: 'center',
									verticalPosition: 'top',
									panelClass: 'bridge-validation-message'
								});
							}
						} else {
							const popUpInput = {
								titleTranslationKey: 'Popup.Confirmation',
								contentTranslationKey: 'ToothEditor.BridgeDeleteWarning',
								iconName: PopupIconNames.Question
							};

							this.popupService
								.openConfirmationPopUp({ popUpInput })
								.pipe(takeUntil(this.componentAlive$))
								.subscribe(result => {
									switch (result) {
										case PopUpActions.Cancel:
											break;
										case PopUpActions.Ok:
											this.removeDefaultValueForBridge(teeth);
											this.dialogRef.close({ action, teeth });
											break;
									}
								});
						}
					} else {
						this.copyToothService.setTooth(teeth[0]);
						this.dialogRef.close({ action, teeth });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	forceSave(): void {
		combineLatest([this.teeth$, this.isBridge$])
			.pipe(
				take(1),
				tap(([teeth, isBridge]: [Tooth[], boolean]) => {
					if (isBridge) {
						const bridgeValidation = this.toothEditorFacade.validateBridge({ teeth });

						if (bridgeValidation.isValid) {
							this.forceUpdateTeeth({ teeth });
						} else {
							this.forceUpdateTeeth({ teeth: [] });
						}
					} else {
						this.forceUpdateTeeth({ teeth });
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	isReadOnlyBannerVisible(): Observable<boolean> {
		return this.toothEditorFacade.isReadOnlyBannerVisible;
	}

	handleImplantTypeIdChange(newImplantTypeId: number | null): void {
		this.toothClickedOn$.pipe(take(1), takeUntil(this.componentAlive$)).subscribe(tooth => {
			if (tooth) {
				this.toothEditorFacade.updateTooth({
					tooth,
					implantTypeId: newImplantTypeId ?? undefined
				});
			}
		});
	}

	onRestorationTypeChanged(ids: RestorationTypeIds): void {
		this.toothClickedOn$.pipe(take(1), takeUntil(this.componentAlive$)).subscribe(tooth => {
			this.toothEditorFacade.updateTooth({ tooth: { ...tooth, ...ids } });
		});
	}

	private forceUpdateTeeth({ teeth }: { teeth: Tooth[] }): void {
		const isBridge = !!teeth[0]?.BridgeIndex;
		let teethToUpdate: Tooth[] = [];

		if (isBridge) {
			teethToUpdate = this.toothEditorFacade.getBridgeTeethToUpdate({
				teethToUpdate: [...teeth],
				teethInJaw: this.data.allTeethInJaw
			});
		} else {
			teethToUpdate = [...teeth];
		}
		this.toothEditorFacade.forceUpdateTeeth({ teethToUpdate });
	}

	private removeDefaultValueForBridge(teeth: Tooth[]) {
		const bridgeIndex = teeth.find(t => !!t.BridgeIndex)?.BridgeIndex;

		if (bridgeIndex && bridgeIndex > 0) {
			this.defaultCrownStore.clearDefaultData(bridgeIndex);
		}
	}

	private handleImplantsViewDataChangedEvent(event: ImplantsViewDataChanged, selectedTooth: Tooth): void {
		this.toothEditorFacade.updateTooth({
			tooth: selectedTooth,
			implantsViewData: {
				...event.data
			}
		});
	}
}
